import React, { useState, useEffect, useCallback, useRef } from "react";
import config from '../config.js';
import ProcessSequencer from '../components/ProcessSequencer';
import { v4 as uuidv4 } from 'uuid';
import { apiErrorMessage } from '../utils/apiError';

// Map a PDF thickness (decimal inches) to the closest thickness option for the
// inferred material_type. Mirrors the helper that used to live in parseAmadaPdf.js
// — kept inline here because the server-side parser returns a numeric thickness
// and the UI owns the (loaded) thickness catalog.
function findMatchingThicknessId(pdfThickness, thicknessOptions, materialType) {
    const pdfVal = parseFloat(pdfThickness);
    if (!Number.isFinite(pdfVal)) return null;
    const TOLERANCE = 0.005;
    const candidates = materialType
        ? thicknessOptions.filter(t => t.material_type === materialType)
        : thicknessOptions;
    let best = null, bestDelta = Infinity;
    for (const opt of candidates) {
        const optVal = parseFloat(opt.thickness_in);
        if (!Number.isFinite(optVal)) continue;
        const delta = Math.abs(optVal - pdfVal);
        if (delta < TOLERANCE && delta < bestDelta) {
            bestDelta = delta;
            best = opt;
        }
    }
    return best ? String(best.thickness_id) : null;
}

// HH:MM:SS → "MM:SS" string for the laser-time field. Mirrors
// parseAmadaPdf.js's convertHHMMSSToMMSS.
function hhmmssToMinutes(hhmmss) {
    if (!hhmmss || typeof hhmmss !== 'string') return "";
    const parts = hhmmss.split(':').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return "";
    const [hh, mm, ss] = parts;
    const totalMinutes = hh * 60 + mm;
    return `${totalMinutes}:${ss.toString().padStart(2, '0')}`;
}

const PartLine = React.memo(({ line, onLineChange, onRemove, allProcessOptions, selectedSteps, setSelectedSteps }) => {
    const handleInputChange = useCallback((field, value) => {
        onLineChange(line.id, field, value);
    }, [line.id, onLineChange]);

    const handleRemove = useCallback(() => {
        onRemove(line.id);
    }, [line.id, onRemove]);

    return (
        <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
                <span>Part: {line.product_id || '(New)'}</span>
                <button className="btn btn-sm btn-outline-danger" onClick={handleRemove}>Remove</button>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Product ID (SKU)</label>
                        <input type="text" className="form-control" value={line.product_id} onChange={(e) => handleInputChange('product_id', e.target.value)} />
                    </div>
                    <div className="col-md-9 mb-3">
                        <label className="form-label">Description</label>
                        <input type="text" className="form-control" value={line.description} onChange={(e) => handleInputChange('description', e.target.value)} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Length (in)</label>
                        <input type="number" step="any" className="form-control" value={line.size_length} onChange={(e) => handleInputChange('size_length', e.target.value)} />
                    </div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Width (in)</label>
                        <input type="number" step="any" className="form-control" value={line.size_width} onChange={(e) => handleInputChange('size_width', e.target.value)} />
                    </div>
                    <div className="col-md-3 mb-3">
                        <label className="form-label">Weight (lbs)</label>
                        <input type="number" step="any" className="form-control" value={line.weight_lbs} onChange={(e) => handleInputChange('weight_lbs', e.target.value)} />
                    </div>
                </div>
                <div className="mb-3">
                    <label className="form-label">Process Sequence</label>
                    <ProcessSequencer
                        allProcesses={allProcessOptions}
                        selectedSteps={selectedSteps}
                        setSelectedSteps={setSelectedSteps}
                    />
                </div>
                <h6>Estimated Process Times</h6>
                <div className="row">
                    {(selectedSteps || []).map(step => {
                        const renderInputForStep = () => {
                           switch (step.name) {
                                case 'Laser':
                                    return <><label className="form-label">Laser Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_laser_minutes} onChange={e => handleInputChange('initial_est_laser_minutes', e.target.value)} /></>;
                                case 'Tap':
                                    return <><label className="form-label">Tap Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_tap_minutes} onChange={e => handleInputChange('initial_est_tap_minutes', e.target.value)} /></>;
                                case 'Brake':
                                    return <><label className="form-label">Brake Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_brake_minutes} onChange={e => handleInputChange('initial_est_brake_minutes', e.target.value)} /></>;
                                case 'Blast':
                                    return <><label className="form-label">Blast Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_blast_minutes} onChange={e => handleInputChange('initial_est_blast_minutes', e.target.value)} /></>;
                                case 'Finish':
                                    return <><label className="form-label">Finish Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_finish_minutes} onChange={e => handleInputChange('initial_est_finish_minutes', e.target.value)} /></>;
                                case 'Cutting':
                                    return <><label className="form-label">Cutting Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_cutting_minutes} onChange={e => handleInputChange('initial_est_cutting_minutes', e.target.value)} /></>;
                                case 'Drilling':
                                    return <><label className="form-label">Drilling Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_drilling_minutes} onChange={e => handleInputChange('initial_est_drilling_minutes', e.target.value)} /></>;
                                case 'Milling':
                                    return <><label className="form-label">Milling Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_milling_minutes} onChange={e => handleInputChange('initial_est_milling_minutes', e.target.value)} /></>;
                                case 'Lathe':
                                    return <><label className="form-label">Lathe Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_lathe_minutes} onChange={e => handleInputChange('initial_est_lathe_minutes', e.target.value)} /></>;
                                case 'Welding':
                                    return <><label className="form-label">Welding Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_weld_minutes} onChange={e => handleInputChange('initial_est_weld_minutes', e.target.value)} /></>;
                                case 'Assembly':
                                    return <><label className="form-label">Assembly Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_assembly_minutes} onChange={e => handleInputChange('initial_est_assembly_minutes', e.target.value)} /></>;
                                case 'Tapping':
                                    return <><label className="form-label">Fab Tapping Est. Time</label><input type="text" className="form-control" placeholder="MM:SS or Mins" value={line.initial_est_fab_tap_minutes} onChange={e => handleInputChange('initial_est_fab_tap_minutes', e.target.value)} /></>;
                                default:
                                    return null;
                            }
                        };
                        return (
                            <div key={step.id || step.name} className="col-md-4 mb-2">
                                {renderInputForStep()}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

export default function BulkPartsEntryPage({ customers, cwtOptions, thicknessOptions }) {
  const [sharedData, setSharedData] = useState({
    customer_id: "",
    material_type: "",
    surface_finish: "",
    thickness_id: "",
    stock_type: "Sheet Stock",
  });
  const [partLines, setPartLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // PDF upload state
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfWarnings, setPdfWarnings] = useState([]);
  const [pdfSuccess, setPdfSuccess] = useState(null);
  const pdfInputRef = useRef(null);

  const API_BASE = config.API_BASE_URL;
  const fabSubProcesses = ["Cutting", "Drilling", "Milling", "Lathe", "Welding", "Assembly", "Tapping"];
  const allProcessOptions = ["Laser", "Tap", "Brake", "Blast", "Finish", ...fabSubProcesses];

  useEffect(() => {
    const updatedPartLines = partLines.map(line => {
      const newProcessSteps = (line.selectedSteps || []).map(step => step.name).join(',');
      if (line.process_steps !== newProcessSteps) {
        return { ...line, process_steps: newProcessSteps };
      }
      return line;
    });

    if (JSON.stringify(updatedPartLines) !== JSON.stringify(partLines)) {
      setPartLines(updatedPartLines);
    }
  }, [partLines]);

  function parseTimeToMinutes(timeStr) {
    const timeValue = String(timeStr || "");
    if (!timeValue || timeValue.trim() === "") return 0;
    const parts = timeValue.split(':').map(part => parseFloat(part.trim())).filter(num => !isNaN(num));
    let totalMinutes = 0;
    if (parts.length === 1) totalMinutes = parts[0];
    else if (parts.length === 2) totalMinutes = parts[0] + (parts[1] / 60.0);
    else if (parts.length >= 3) totalMinutes = (parts[0] * 60) + parts[1] + (parts[2] / 60.0);
    else return 0;
    return totalMinutes >= 0 ? parseFloat(totalMinutes.toFixed(2)) : 0;
  }

  const handleSharedChange = (e) => {
    const { name, value } = e.target;
    setSharedData(prev => ({ ...prev, [name]: value }));
  };

  const createEmptyLine = () => ({
    id: uuidv4(), product_id: "", description: "", selectedSteps: [],
    size_length: "", size_width: "", weight_lbs: "",
    initial_est_laser_minutes: "", initial_est_tap_minutes: "",
    initial_est_brake_minutes: "", initial_est_blast_minutes: "", initial_est_finish_minutes: "",
    initial_est_cutting_minutes: "", initial_est_drilling_minutes: "", initial_est_milling_minutes: "",
    initial_est_lathe_minutes: "", initial_est_weld_minutes: "", initial_est_assembly_minutes: "",
    initial_est_fab_tap_minutes: "",
  });

  const handleAddLine = () => {
    setPartLines(prev => [...prev, createEmptyLine()]);
  };

  const handleRemoveLine = useCallback((id) => {
    setPartLines(prev => prev.filter(line => line.id !== id));
  }, []);

  const handleLineChange = useCallback((id, field, value) => {
    setPartLines(prev =>
      prev.map(line => (line.id === id ? { ...line, [field]: value } : line))
    );
  }, []);

  // --- PDF Upload Handlers ---

  const handlePdfFileSelected = (e) => {
    setPdfFile(e.target.files[0] || null);
    setPdfError(null);
    setPdfWarnings([]);
    setPdfSuccess(null);
  };

  const handleParsePdf = async () => {
    if (!pdfFile) return;
    setPdfParsing(true);
    setPdfError(null);
    setPdfWarnings([]);
    setPdfSuccess(null);

    try {
      // Single source of truth for Amada PDF parsing — the deterministic
      // multi-sheet parser. We don't need owner_customer_id here (it scopes
      // material match candidates which we don't surface in the parts catalog
      // flow); the server skips that step when it's omitted.
      const fd = new FormData();
      fd.append('file', pdfFile);
      const url = `${config.API_BASE_URL}/cut_sheets/parse_amada_report`;
      const res = await fetch(url, { method: 'POST', body: fd });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, `Parse failed (${res.status})`));
      }
      const result = await res.json();

      const programs = result.programs || [];
      if (programs.length === 0) {
        throw new Error('No cut-sheet programs were parsed from this PDF. Check that it is an Amada Sheet Report.');
      }

      // Auto-set shared attributes from the FIRST program (when the PDF spans
      // multiple sheets they typically share material + thickness).
      const firstProg = programs[0];
      const availableTypes = cwtOptions?.material_types || [];
      const inferredType = firstProg.material_type && availableTypes.includes(firstProg.material_type)
        ? firstProg.material_type
        : null;
      const inferredThicknessId = firstProg.thickness != null
        ? findMatchingThicknessId(firstProg.thickness, thicknessOptions, inferredType || firstProg.material_type)
        : null;

      if (inferredType || inferredThicknessId) {
        setSharedData(prev => ({
          ...prev,
          ...(inferredType ? { material_type: inferredType } : {}),
          ...(inferredThicknessId ? { thickness_id: inferredThicknessId } : {}),
        }));
      }

      // Flatten parts across all programs and dedupe by product_id, keeping the
      // first occurrence (matches the legacy parseAmadaPdf behavior).
      const seen = new Set();
      const flatParts = [];
      for (const prog of programs) {
        for (const p of (prog.parts || [])) {
          const pid = p.product_id;
          if (!pid || seen.has(pid)) continue;
          seen.add(pid);
          flatParts.push(p);
        }
      }

      const newLines = flatParts.map(part => {
        const steps = [{
          id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}-Laser`,
          name: 'Laser'
        }];
        if (part.bending) {
          steps.push({
            id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 6)}-Brake`,
            name: 'Brake'
          });
        }
        return {
          ...createEmptyLine(),
          product_id: part.product_id,
          selectedSteps: steps,
          size_length: part.size_length || "",
          size_width: part.size_width || "",
          weight_lbs: part.weight_lbs ? String(part.weight_lbs) : "",
          initial_est_laser_minutes: hhmmssToMinutes(part.process_time),
        };
      });

      setPartLines(prev => [...prev, ...newLines]);
      const warningMessages = [];
      if (firstProg.material_type && !inferredType) {
        warningMessages.push(`Could not auto-match material "${firstProg.material_type}". Please select material type manually.`);
      }
      if (firstProg.thickness != null && !inferredThicknessId) {
        warningMessages.push(`Could not match thickness ${firstProg.thickness}" to available options. Please select manually.`);
      }
      warningMessages.push(...(result.warnings || []));
      setPdfWarnings(warningMessages);
      setPdfSuccess(`Successfully imported ${newLines.length} parts from PDF.`);

      // Reset file input
      if (pdfInputRef.current) pdfInputRef.current.value = "";
      setPdfFile(null);

    } catch (err) {
      setPdfError(`Failed to parse PDF: ${err.message}`);
    }

    setPdfParsing(false);
  };

  // --- Bulk Create Handler ---

  const handleBulkCreate = async () => {
    if (partLines.length === 0) {
      setError("Please add at least one part to create.");
      return;
    }
    setLoading(true);
    setError(null);
    setResults(null);
    const creationResults = [];
    for (const line of partLines) {
      if (!line.product_id.trim()) {
        creationResults.push({ success: false, product_id: "N/A", message: "Product ID is required." });
        continue;
      }
      const process_steps = line.selectedSteps.map(step => step.name).join(',');
      const payload = {
        ...sharedData, ...line, process_steps,
        size_length: parseFloat(line.size_length) || 0,
        size_width: parseFloat(line.size_width) || 0,
        weight_lbs: parseFloat(line.weight_lbs) || 0,
        initial_est_laser_minutes: parseTimeToMinutes(line.initial_est_laser_minutes),
        initial_est_tap_minutes: parseTimeToMinutes(line.initial_est_tap_minutes),
        initial_est_brake_minutes: parseTimeToMinutes(line.initial_est_brake_minutes),
        initial_est_blast_minutes: parseTimeToMinutes(line.initial_est_blast_minutes),
        initial_est_finish_minutes: parseTimeToMinutes(line.initial_est_finish_minutes),
        initial_est_cutting_minutes: parseTimeToMinutes(line.initial_est_cutting_minutes),
        initial_est_drilling_minutes: parseTimeToMinutes(line.initial_est_drilling_minutes),
        initial_est_milling_minutes: parseTimeToMinutes(line.initial_est_milling_minutes),
        initial_est_lathe_minutes: parseTimeToMinutes(line.initial_est_lathe_minutes),
        initial_est_weld_minutes: parseTimeToMinutes(line.initial_est_weld_minutes),
        initial_est_assembly_minutes: parseTimeToMinutes(line.initial_est_assembly_minutes),
        initial_est_fab_tap_minutes: parseTimeToMinutes(line.initial_est_fab_tap_minutes),
      };
      delete payload.id;
      delete payload.selectedSteps;
      try {
        const res = await fetch(`${API_BASE}/parts`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!res.ok) {
          throw new Error(await apiErrorMessage(res, "Failed to create part"));
        }
        const createdPart = await res.json();
        creationResults.push({ success: true, product_id: line.product_id, part_id: createdPart.part_id });
      } catch (err) {
        creationResults.push({ success: false, product_id: line.product_id, message: err.message });
      }
    }
    setLoading(false);
    setResults(creationResults);
    setPartLines([]);
  };

  const filteredThicknessOptions = thicknessOptions.filter(
    (opt) => opt.material_type === sharedData.material_type
  );

  return (
    <div>
      <h4>Bulk Part Entry</h4>
      {error && <div className="alert alert-danger">{error}</div>}
      {results && (
        <div className="alert alert-info">
          <h5>Creation Summary:</h5>
          <ul>{results.map((res, idx) => (<li key={idx} className={res.success ? 'text-success' : 'text-danger'}><strong>SKU: {res.product_id}</strong> - {res.success ? `Created successfully (Part ID: ${res.part_id})` : `Failed: ${res.message}`}</li>))}</ul>
        </div>
      )}

      {/* PDF Import Card */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-bold">Import from Amada PDF</span>
          <span className="badge bg-secondary">Optional</span>
        </div>
        <div className="card-body">
          <p className="text-muted small mb-2">
            Upload a Parts Info Construction Sheet PDF to auto-populate part lines below.
          </p>
          <div className="input-group">
            <input
              type="file"
              className="form-control"
              accept=".pdf"
              onChange={handlePdfFileSelected}
              ref={pdfInputRef}
            />
            <button
              className="btn btn-info"
              onClick={handleParsePdf}
              disabled={!pdfFile || pdfParsing}
            >
              {pdfParsing ? 'Parsing...' : 'Upload & Populate'}
            </button>
          </div>
          {pdfError && <div className="text-danger small mt-2">{pdfError}</div>}
          {pdfSuccess && <div className="text-success small mt-2">{pdfSuccess}</div>}
          {pdfWarnings.length > 0 && (
            <div className="mt-2">
              {pdfWarnings.map((w, i) => <div key={i} className="text-warning small">{w}</div>)}
            </div>
          )}
        </div>
      </div>

      {/* Shared Attributes Card */}
      <div className="card mb-4">
        <h5 className="card-header">Common Attributes for All Parts</h5>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3 mb-3"><label className="form-label">Customer</label><select className="form-select" name="customer_id" value={sharedData.customer_id} onChange={handleSharedChange}><option value="">-- Select Customer --</option>{customers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}</select></div>
            <div className="col-md-3 mb-3"><label className="form-label">Stock Type</label><select className="form-select" name="stock_type" value={sharedData.stock_type} onChange={handleSharedChange}><option value="Sheet Stock">Sheet Stock</option><option value="Shape Stock">Shape Stock</option></select></div>
            <div className="col-md-2 mb-3"><label className="form-label">Material Type</label><select className="form-select" name="material_type" value={sharedData.material_type} onChange={handleSharedChange}><option value="">Select...</option>{(cwtOptions?.material_types || []).map(type => <option key={type} value={type}>{type}</option>)}</select></div>
            <div className="col-md-2 mb-3"><label className="form-label">Surface Finish</label><select className="form-select" name="surface_finish" value={sharedData.surface_finish} onChange={handleSharedChange}><option value="">Select...</option>{(cwtOptions?.surface_finishes || []).map(finish => <option key={finish} value={finish}>{finish}</option>)}</select></div>
            <div className="col-md-2 mb-3"><label className="form-label">Thickness</label><select className="form-select" name="thickness_id" value={sharedData.thickness_id} onChange={handleSharedChange} disabled={!sharedData.material_type}><option value="">Select...</option>{filteredThicknessOptions.map(opt => <option key={opt.thickness_id} value={opt.thickness_id}>{opt.display_thickness}</option>)}</select></div>
          </div>
        </div>
      </div>

      {/* Part Lines */}
      {partLines.map((line) => {
        const lineSelectedSteps = Array.isArray(line.selectedSteps) ? line.selectedSteps : [];

        const setLineSelectedSteps = (updater) => {
          const newSteps = typeof updater === 'function' ? updater(lineSelectedSteps) : updater;
          handleLineChange(line.id, 'selectedSteps', newSteps);
        };

        return (
          <PartLine
            key={line.id}
            line={line}
            onLineChange={handleLineChange}
            onRemove={handleRemoveLine}
            allProcessOptions={allProcessOptions}
            selectedSteps={lineSelectedSteps}
            setSelectedSteps={setLineSelectedSteps}
          />
        );
      })}

      <button className="btn btn-outline-secondary" onClick={handleAddLine}>+ Add Part Line</button>
      <hr />
      <button className="btn btn-primary btn-lg" onClick={handleBulkCreate} disabled={loading || partLines.length === 0}>
        {loading ? 'Creating...' : `Create ${partLines.length} Parts`}
      </button>
    </div>
  );
}
